import React, { useState, useEffect, useCallback } from 'react';

const API = process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5001`;

const VOLTAGE_CLASSES = [
  { id: 'LV', label: 'LV (400V)', color: '#F5C542', volts: '400V' },
  { id: 'MV_11kV', label: 'MV (11kV)', color: '#E8B828', volts: '11kV' },
  { id: 'MV_33kV', label: 'MV (33kV)', color: '#D4A020', volts: '33kV' },
  { id: 'HV_132kV', label: 'HV (132kV)', color: '#B88A14', volts: '132kV' },
  { id: 'EHV_400kV', label: 'EHV (400kV)', color: '#A07A0E', volts: '400kV' },
];

const TYPE_LABELS = {
  overhead_acsr: 'Overhead ACSR',
  overhead_aac: 'Overhead AAC',
  overhead_aaac: 'Overhead AAAC',
  underground_xlpe: 'Underground XLPE',
  underground_pilc: 'Underground PILC',
};

const ConductorCatalog = ({ onBack, selectionMode, onSelect }) => {
  const [conductors, setConductors] = useState([]);
  const [summary, setSummary] = useState([]);
  const [activeClass, setActiveClass] = useState(null);
  const [selectedConductor, setSelectedConductor] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [cRes, sRes] = await Promise.all([
        fetch(`${API}/api/conductors`),
        fetch(`${API}/api/conductors/voltage-classes`),
      ]);
      if (cRes.ok) setConductors(await cRes.json());
      if (sRes.ok) setSummary(await sRes.json());
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filtered = activeClass
    ? conductors.filter(c => c.voltage_class === activeClass)
    : conductors;

  // ===== Conductor detail panel =====
  if (selectedConductor) {
    const c = selectedConductor;
    const vcInfo = VOLTAGE_CLASSES.find(v => v.id === c.voltage_class) || {};
    return (
      <div className="def-editor">
        <div className="def-editor-header">
          <div className="def-editor-title">
            <div className="node-color-dot" style={{ background: vcInfo.color || '#666', width: 32, height: 32, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.3)' }} />
            <div>
              <h2>{c.name}</h2>
              <span className="def-editor-id">
                {c.code_name && `${c.code_name} \u00B7 `}{c.material} \u00B7 {c.cross_section_mm2}mm\u00B2 \u00B7 {vcInfo.label}
              </span>
            </div>
          </div>
          <div className="def-editor-actions">
            <button className="btn-ghost" onClick={() => setSelectedConductor(null)}>Back</button>
          </div>
        </div>

        <div className="def-editor-body">
          <div className="def-editor-left">
            {/* Electrical */}
            <div className="def-section">
              <h3>Electrical Properties</h3>
              <div className="conductor-props-grid">
                <PropRow label="DC Resistance (20°C)" value={c.dc_resistance_20c_ohm_per_km} unit={'\u03A9/km'} />
                <PropRow label="AC Resistance (50°C)" value={c.ac_resistance_50c_ohm_per_km} unit={'\u03A9/km'} />
                <PropRow label="AC Resistance (75°C)" value={c.ac_resistance_75c_ohm_per_km} unit={'\u03A9/km'} />
                {c.ac_resistance_90c_ohm_per_km && <PropRow label="AC Resistance (90°C)" value={c.ac_resistance_90c_ohm_per_km} unit={'\u03A9/km'} />}
                <PropRow label="Reactance (X)" value={c.reactance_ohm_per_km} unit={'\u03A9/km'} />
                <PropRow label="Susceptance (B)" value={c.susceptance_us_per_km} unit={'\u03BCS/km'} />
                {c.capacitance_uf_per_km && <PropRow label="Capacitance" value={c.capacitance_uf_per_km} unit={'\u03BCF/km'} />}
              </div>
            </div>

            {/* Ratings */}
            <div className="def-section">
              <h3>Current Ratings</h3>
              <div className="conductor-props-grid">
                <PropRow label="Thermal Rating (continuous)" value={c.thermal_rating_a} unit="A" highlight />
                <PropRow label="Emergency Rating" value={c.emergency_rating_a} unit="A" />
                <PropRow label="Max Operating Temp" value={c.max_operating_temp_c} unit="°C" />
              </div>
            </div>

            {/* PyPSA mapping */}
            <div className="pypsa-box">
              <h3>PyPSA Line Parameters</h3>
              <div className="pypsa-params">
                <div className="pypsa-param"><span className="pypsa-key">type</span><span className="pypsa-val">{c.type.includes('underground') ? 'Cable' : 'Line'}</span></div>
                <div className="pypsa-param"><span className="pypsa-key">r</span><span className="pypsa-val">{c.ac_resistance_75c_ohm_per_km?.toFixed(4) || '?'} {'\u03A9/km'}</span></div>
                <div className="pypsa-param"><span className="pypsa-key">x</span><span className="pypsa-val">{c.reactance_ohm_per_km?.toFixed(4) || '?'} {'\u03A9/km'}</span></div>
                <div className="pypsa-param"><span className="pypsa-key">b</span><span className="pypsa-val">{c.susceptance_us_per_km ? (c.susceptance_us_per_km * 1e-6).toExponential(3) : '?'} S/km</span></div>
                <div className="pypsa-param"><span className="pypsa-key">s_nom</span><span className="pypsa-val">(per instance)</span></div>
              </div>
            </div>
          </div>

          <div className="def-editor-right">
            {/* Physical */}
            <div className="def-section">
              <h3>Physical Properties</h3>
              <div className="conductor-props-grid">
                <PropRow label="Cross Section (Al)" value={c.cross_section_mm2} unit="mm\u00B2" />
                {c.steel_section_mm2 && <PropRow label="Steel Section" value={c.steel_section_mm2} unit="mm\u00B2" />}
                <PropRow label="Overall Diameter" value={c.overall_diameter_mm} unit="mm" />
                <PropRow label="Weight" value={c.weight_kg_per_km} unit="kg/km" />
                {c.breaking_load_kn && <PropRow label="Breaking Load" value={c.breaking_load_kn} unit="kN" />}
                {c.typical_span_m && <PropRow label="Typical Span" value={c.typical_span_m} unit="m" />}
              </div>
            </div>

            {/* Construction */}
            <div className="def-section">
              <h3>Construction</h3>
              <div className="conductor-props-grid">
                <PropRow label="Material" value={c.material} />
                <PropRow label="Type" value={TYPE_LABELS[c.type] || c.type} />
                {c.stranding && <PropRow label="Stranding" value={c.stranding} />}
                {c.num_cores && <PropRow label="Cores" value={c.num_cores} />}
                {c.insulation && <PropRow label="Insulation" value={c.insulation} />}
                {c.armour && <PropRow label="Armour" value={c.armour} />}
                <PropRow label="Standard" value={c.standard} />
              </div>
            </div>

            {c.notes && (
              <div className="def-section">
                <h3>Notes</h3>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 }}>{c.notes}</div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ===== Main catalog view =====
  if (loading) return <div className="def-list-view"><div className="def-list-header"><h1>Conductor Catalog</h1><p className="def-list-subtitle">Loading...</p></div></div>;

  return (
    <div className="def-list-view">
      <div className="def-list-header">
        <div>
          <h1>{'\u26A1'} {selectionMode ? 'Select Conductor for New Line' : 'Conductor & Cable Catalog'}</h1>
          <p className="def-list-subtitle">
            {selectionMode
              ? 'Click a conductor to create a new line definition with its properties pre-filled.'
              : 'South African standard conductors and cables. Reference data for line definitions and loss calculations.'}
            <br/><span style={{ color: 'var(--cenec-green)', fontSize: 11 }}>{conductors.length} conductors across {summary.length} voltage classes</span>
          </p>
        </div>
        {onBack && <button className="btn-ghost" onClick={onBack}>{'← Back'}</button>}
      </div>

      {/* Voltage class cards */}
      <div className="conductor-class-grid">
        {VOLTAGE_CLASSES.map(vc => {
          const s = summary.find(x => x.voltage_class === vc.id);
          const isActive = activeClass === vc.id;
          return (
            <button
              key={vc.id}
              className={`conductor-class-card ${isActive ? 'active' : ''}`}
              style={{ borderTopColor: vc.color }}
              onClick={() => setActiveClass(isActive ? null : vc.id)}
            >
              <div className="conductor-class-volts" style={{ color: vc.color }}>{vc.volts}</div>
              <div className="conductor-class-count">{s?.count || 0}</div>
              <div className="conductor-class-label">{vc.label}</div>
              {s && <div className="conductor-class-range">{s.min_rating}{'–'}{s.max_rating} A</div>}
            </button>
          );
        })}
      </div>

      {/* Conductor table */}
      <div className="conductor-table-wrap">
        <table className="conductor-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Code</th>
              <th>Type</th>
              <th>mm{'\u00B2'}</th>
              <th>R{'\u2082\u2080'} ({'\u03A9/km'})</th>
              <th>R{'\u2087\u2085'} ({'\u03A9/km'})</th>
              <th>X ({'\u03A9/km'})</th>
              <th>Rating (A)</th>
              <th>Class</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(c => {
              const vcInfo = VOLTAGE_CLASSES.find(v => v.id === c.voltage_class) || {};
              return (
                <tr key={c.id} onClick={() => selectionMode && onSelect ? onSelect(c) : setSelectedConductor(c)} style={{ cursor: 'pointer' }}>
                  <td className="conductor-name">{c.name}</td>
                  <td className="mono" style={{ color: vcInfo.color }}>{c.code_name || '—'}</td>
                  <td><span className={`conductor-type-badge ${c.type.includes('underground') ? 'ug' : 'oh'}`}>{c.type.includes('underground') ? 'UG' : 'OH'}</span></td>
                  <td className="mono">{c.cross_section_mm2}</td>
                  <td className="mono">{c.dc_resistance_20c_ohm_per_km?.toFixed(4)}</td>
                  <td className="mono">{c.ac_resistance_75c_ohm_per_km?.toFixed(4)}</td>
                  <td className="mono">{c.reactance_ohm_per_km?.toFixed(3)}</td>
                  <td className="mono" style={{ fontWeight: 600, color: vcInfo.color }}>{c.thermal_rating_a}</td>
                  <td><span className="conductor-vc-badge" style={{ background: vcInfo.color }}>{vcInfo.volts}</span></td>
                  <td>
                    {selectionMode && onSelect
                      ? <button className="btn-accent" style={{ padding: '4px 10px', fontSize: 11 }} onClick={(e) => { e.stopPropagation(); onSelect(c); }}>Select</button>
                      : <button className="btn-card" onClick={(e) => { e.stopPropagation(); setSelectedConductor(c); }}>View</button>
                    }
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/* Property row helper */
const PropRow = ({ label, value, unit, highlight }) => (
  <div className={`conductor-prop ${highlight ? 'highlight' : ''}`}>
    <span className="conductor-prop-label">{label}</span>
    <span className="conductor-prop-value">
      {value != null ? (typeof value === 'number' ? value : value) : '—'}
      {unit && value != null && <span className="conductor-prop-unit"> {unit}</span>}
    </span>
  </div>
);

export default ConductorCatalog;
